import React, { useState } from 'react';
import {
	Box,
	Button,
	Menu,
	MenuItem,
	styled,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Theme,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ContainerBox from 'common/components/layout/ContainerBox';
import { useTranslation } from 'services/localization';
import { useDispatch, useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import ConnectDomainModal from './ConnectDomainModal';
import { isDevEnv } from 'common/utils/common';
import { Callable } from 'common/frontend/callable';
import useShopFormat from 'hooks/useShopFormat';
import LearnMore from 'components/LearnMore';
import { RiArrowDownSLine } from 'react-icons/ri';
import BackdropDialog from 'common/components/BackdropDialog';
import { createStyles } from '@mui/styles';
import { CustomDomain } from 'common/modules/customDomains/types/dbDocument';
import Tag from 'components/Tag';
import { useHasFeature } from 'hooks';
import { api } from 'common/frontend/api';
import HasFeature from 'components/features/HasFeature';
import DomainsPromoPage from 'components/features/promos/DomainsPromoPage';

type DomainRowStatus =
	| 'connected'
	| 'pending'
	| 'needs_setup'
	| 'failed'
	| 'refreshing'
	| 'deleting';
interface DomainRow {
	id: string;
	domainName: string;
	status: DomainRowStatus;
	date: string | null;
	provider: 'rentle' | 'third-party';
	primary: boolean;
}

const RENTLE_DOMAIN_ID = '__rentle_hosted_domain__';

interface Props {}
const Domains = (props: Props) => {
	const { localFormat } = useShopFormat();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const classes = useStyles();
	const theme = useTheme();
	const { hasFeature } = useHasFeature();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopUrls = useSelector(ShopSelectors.activeShopUrls);
	const shopCustomDomainDetails = useSelector(ShopSelectors.shopCustomDomainDetails);
	const [connectDomainModalOpen, setConnectDomainModelOpen] = useState<string | boolean>(false);
	const [confirmDeleteDomain, setConfirmDeleteDomain] = useState<{
		id: string;
		domainName: string;
	} | null>(null);
	const maxCustomDomains = hasFeature('ADDITIONAL_CUSTOM_DOMAIN', { scope: 'shop' }) ? 2 : 1;
	const customDomainsCount = Object.keys(shopCustomDomainDetails ?? {}).length;
	const addingDomainDisabled = customDomainsCount >= maxCustomDomains;
	const primaryCustomDomain = shopUrls?.primaryCustomDomain;
	const [refreshingDomainIds, setRefreshingDomainIds] = useState<string[]>([]);
	const [deletingDomainIds, setDeletingDomainIds] = useState<string[]>([]);

	const rentleDomainToDomainRow = (): DomainRow => {
		const urlPrefix = isDevEnv() ? 'dev.' : '';
		const rentleDomainName = shopUrls?.storeUrl
			? `${urlPrefix}rentle.store/${shopUrls.storeUrl}`
			: '';
		return {
			id: RENTLE_DOMAIN_ID,
			domainName: rentleDomainName,
			status: 'connected',
			date: null,
			provider: 'rentle',
			primary: !primaryCustomDomain,
		};
	};

	const domainStatusToDomainRowStatus = (status: CustomDomain['status']): DomainRow['status'] => {
		const statusMap: Record<CustomDomain['status'], DomainRow['status']> = {
			needs_setup: 'needs_setup',
			pending: 'pending',
			active: 'connected',
		};
		return statusMap[status];
	};

	const cloudflareDomainsToDomainRow = (): DomainRow[] => {
		const domainRows: DomainRow[] = Object.entries(shopCustomDomainDetails ?? {}).map(
			([domainId, record]) => {
				const isRefresing = refreshingDomainIds.includes(domainId);
				const idDeleting = deletingDomainIds.includes(domainId);
				const status: DomainRowStatus = isRefresing
					? 'refreshing'
					: idDeleting
					? 'deleting'
					: domainStatusToDomainRowStatus(record.status);
				return {
					id: domainId,
					domainName: record.hostname,
					status,
					date: record.created,
					provider: 'third-party',
					primary: primaryCustomDomain === record.hostname,
				};
			},
		);
		return domainRows.sort((d1, d2) => (!d1.date ? -1 : !d2.date ? 1 : d1.date < d2.date ? -1 : 1));
	};

	const openNewDomainModal = () => {
		setConnectDomainModelOpen(true);
	};

	const openExistingDomainModel = (domainName: string) => {
		setConnectDomainModelOpen(domainName);
	};

	const closeNewDomainModal = () => {
		setConnectDomainModelOpen(false);
	};

	const handleDeleteDomain = async (domainId: string) => {
		setDeletingDomainIds((ids) => [...ids, domainId]);
		try {
			await Callable.customDomains.deleteCustomDomain({
				domainId,
				merchantId: shopId,
			});
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					variant: 'error',
					message: t('common:errors.somethingWentWrong'),
				}),
			);
		} finally {
			setDeletingDomainIds((ids) => ids.filter((id) => id !== domainId));
		}
	};

	const handleDomainRecordRefresh = async (domainId: string) => {
		setRefreshingDomainIds((ids) => [...ids, domainId]);
		try {
			await Callable.customDomains.refreshDomainRecords({
				domainId,
				merchantId: shopId,
			});
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					variant: 'error',
					message: t('common:errors.somethingWentWrong'),
				}),
			);
		} finally {
			setRefreshingDomainIds((ids) => ids.filter((id) => id !== domainId));
		}
	};

	const setDomainAsPrimary = async (args: { domainId: string; domainName: string }) => {
		const storeBaseUrl = shopUrls?.storeUrl;
		if (!storeBaseUrl) return;
		try {
			await api()
				.shopUrls.doc(storeBaseUrl)
				.update({
					primaryCustomDomain: args.domainId === RENTLE_DOMAIN_ID ? null : args.domainName,
				});
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					variant: 'error',
					message: t('common:errors.somethingWentWrong'),
				}),
			);
		}
	};

	const renderDomainStatus = (status: DomainRow['status']) => {
		const statusBubble: Record<DomainRow['status'], JSX.Element> = {
			needs_setup: <StatusBubble variant="error" />,
			connected: <StatusBubble variant="success" />,
			pending: <StatusBubble variant="danger" />,
			failed: <StatusBubble variant="error" />,
			deleting: <StatusBubble variant="neutral" />,
			refreshing: <StatusBubble variant="neutral" />,
		};
		const statusText: Record<DomainRow['status'], string> = {
			needs_setup: t('statuses.needsSetup', 'Needs setup'),
			connected: t('statuses.connected', 'Connected'),
			pending: t('statuses.verifying', 'Verifying'),
			failed: t('statuses.failed', 'Failed'),
			deleting: `${t('statuses.deleting', 'Deleting')}...`,
			refreshing: `${t('statuses.refreshing', 'Refreshing')}...`,
		};
		const verifyingInfo = t(
			'account.domains.verifyingDomainInfo',
			'We are securing your site. This is usually done within a few hours. No action required.',
		);
		return (
			<Tooltip
				title={status !== 'pending' ? '' : verifyingInfo}
				classes={{ tooltip: classes.tooltip }}
			>
				<Box display="flex" alignItems="center">
					{statusBubble[status]}
					<Typography variant="body2" sx={{ ml: 1, fontWeight: 400 }}>
						{statusText[status]}
					</Typography>
				</Box>
			</Tooltip>
		);
	};

	const domainRows = [rentleDomainToDomainRow(), ...cloudflareDomainsToDomainRow()];

	const getProviderString = (provider: DomainRow['provider']) => {
		const providersMap: Record<DomainRow['provider'], string> = {
			rentle: 'Rentle',
			'third-party': t('thirdParty', 'Third-party'),
		};
		return providersMap[provider];
	};

	return (
		<HasFeature requiredFeature="CUSTOM_DOMAINS" fallback={<DomainsPromoPage />}>
			<Box>
				<ContainerBox padding="none">
					<Box sx={{ overflow: 'auto' }}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>{t('common:form.name')}</TableCell>
									<TableCell>{t('statuses.status', 'Status')}</TableCell>
									<TableCell>{t('common.dateAdded', 'Date added')}</TableCell>
									<TableCell>{t('account.domains.provider', 'Provider')}</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{domainRows.map(({ id, domainName, status, date, provider, primary }) => (
									<TableRow key={domainName}>
										<TableCell component="th" scope="row">
											<Box display="flex" alignItems="center">
												{domainName}
												{!!primary && (
													<Tag text={t('account.domains.primary')} sx={{ ml: 1 }} color="success" />
												)}
											</Box>
										</TableCell>
										<TableCell>{renderDomainStatus(status)}</TableCell>
										<TableCell>{date ? localFormat(date, 'DD.MM.YYYY') : ''}</TableCell>
										<TableCell>{getProviderString(provider)}</TableCell>
										<TableCell align="right" padding="checkbox" sx={{ whiteSpace: 'nowrap' }}>
											{status === 'needs_setup' && !isMobile ? (
												<Button
													variant="text"
													onClick={() => openExistingDomainModel(domainName)}
													sx={{ minWidth: 0, paddingX: 2 }}
												>
													{t('account.domains.view', 'View')}
												</Button>
											) : null}
											{id === RENTLE_DOMAIN_ID && !!primary ? null : (
												<DomainEditMenu
													domainId={id}
													domainStatus={status}
													isPrimary={!!primary}
													onDelete={() => setConfirmDeleteDomain({ id, domainName })}
													onRefresh={() => handleDomainRecordRefresh(id)}
													onSetupClick={() => openExistingDomainModel(domainName)}
													onSetPrimaryClick={() => setDomainAsPrimary({ domainId: id, domainName })}
												/>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				</ContainerBox>
				<Tooltip
					title={
						!addingDomainDisabled
							? ''
							: t('account.domains.maxDomainCountInfo', {
									defaultValue: `You can connect only {{count}} third-party domain`,
									count: maxCustomDomains,
							  })
					}
					classes={{ tooltip: classes.tooltip }}
				>
					<Box display="inline-block" sx={{ mt: 1 }}>
						<Button variant="outlined" onClick={openNewDomainModal} disabled={addingDomainDisabled}>
							{t('account.domains.connectExisting', 'Connect existing domain')}
						</Button>
					</Box>
				</Tooltip>
				<Box mt={4}>
					<LearnMore linkHref="https://support.rentle.io/en/custom-domain" labelVariant="domains" />
				</Box>
				{connectDomainModalOpen && (
					<ConnectDomainModal
						onClose={closeNewDomainModal}
						domain={typeof connectDomainModalOpen === 'string' ? connectDomainModalOpen : undefined}
						shopId={shopId}
					/>
				)}
				{!!confirmDeleteDomain && (
					<BackdropDialog
						isOpen={!!confirmDeleteDomain}
						onClose={() => setConfirmDeleteDomain(null)}
						label={t('account.domains.deleteDomainTitle', 'Delete domain')}
						body={
							<Box>
								<Typography variant="body2">
									{t(
										'account.domains.deleteDomainBody',
										`Are you sure you want to delete the domain`,
									)}{' '}
									{confirmDeleteDomain.domainName}?
								</Typography>
							</Box>
						}
						variant="danger"
						width="xs"
						confirm={{
							label: t('common:actions.delete'),
							callback: () => handleDeleteDomain(confirmDeleteDomain.id),
						}}
						cancel={{
							label: t('common:actions.cancel'),
						}}
					/>
				)}
			</Box>
		</HasFeature>
	);
};

const DomainEditMenu = ({
	domainId,
	domainStatus,
	isPrimary,
	onDelete,
	onRefresh,
	onSetupClick,
	onSetPrimaryClick,
}: {
	domainId: string;
	domainStatus: DomainRow['status'];
	isPrimary: boolean;
	onDelete: () => void;
	onSetupClick: () => void;
	onRefresh: () => void;
	onSetPrimaryClick: () => void;
}) => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = !!anchorEl;
	const isDeleting = domainStatus === 'deleting';
	const isRentleDomain = domainId === RENTLE_DOMAIN_ID;
	const showDelete = !isRentleDomain;
	const showRefresh = !isRentleDomain && domainStatus === 'pending';
	const showSetup = !isRentleDomain && domainStatus === 'needs_setup';
	const showSetPrimary = domainStatus === 'connected';

	const handleEditClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleDeleteClick = () => {
		onDelete();
		handleClose();
	};

	const handleSetupClick = () => {
		onSetupClick();
		handleClose();
	};

	const handleRefreshClick = () => {
		onRefresh();
		handleClose();
	};

	const handleSetPrimaryClick = () => {
		onSetPrimaryClick();
		handleClose();
	};
	if (isDeleting) return null;
	if (!showDelete && !showRefresh && !showSetup && !showSetPrimary) return null;

	return (
		<>
			<Button
				endIcon={<RiArrowDownSLine />}
				onClick={handleEditClick}
				sx={{ minWidth: 0, paddingX: 2 }}
			>
				{t('common:actions.edit')}
			</Button>
			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				{showRefresh && (
					<MenuItem onClick={handleRefreshClick}>
						{t('account.domains.refreshStatus', 'Refresh domain status')}
					</MenuItem>
				)}
				{showSetup && (
					<MenuItem onClick={handleSetupClick}>
						{t('account.domains.viewSetupInfo', 'View setup information')}
					</MenuItem>
				)}
				{showSetPrimary && (
					<MenuItem onClick={handleSetPrimaryClick} disabled={isPrimary}>
						{t('account.domains.setAsPrimary', 'Set as primary')}
					</MenuItem>
				)}
				{showDelete && (
					<MenuItem onClick={handleDeleteClick} sx={{ color: (theme) => theme.palette.error.main }}>
						{t('common:actions.delete')}
					</MenuItem>
				)}
			</Menu>
		</>
	);
};

const StatusBubble = ({ variant }: { variant: 'success' | 'danger' | 'error' | 'neutral' }) => {
	const Bubble = styled('span')(({ theme }) => ({
		width: 8,
		height: 8,
		display: 'block',
		borderRadius: '50%',
		backgroundColor:
			variant === 'success'
				? theme.palette.success.dark
				: variant === 'danger'
				? theme.palette.warning.main
				: variant === 'error'
				? theme.palette.error.main
				: theme.palette.background.secondary,
	}));
	return <Bubble />;
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		tooltip: {
			background: theme.palette.common.black,
		},
	}),
);

export default Domains;
